/* `/evals` — an agent's suite of cases and checks, and the runs that score it.
   `backend/modules/evals/README.md` for the wire.

   In `shared/api/` because the Evals tab, the publish gate (`shared/components`)
   and the app shell's "Add to evals" all use it. The mappers are the one place
   the wire meets the mockup's own `EvalCase` / `EvalCheck` / `EvalRun`, so the
   screens that rendered the local engine render the server's runs unchanged.

   A run is graded by the eval worker case by case, so while one is `running`
   the suite and that run are re-read every POLL_MS. */
import { useMutation, useQueries, useQuery, useQueryClient } from "@tanstack/react-query";

import { client } from "./client";
import { unwrap, type Envelope } from "./envelope";
import { ageLabel } from "./threads";
import { CHECK_TYPES } from "@/shared/lib/evals-model";

import type {
  EvalCase, EvalCaseCreate, EvalCasePatch, EvalCaseRow, EvalCheck, EvalCheckIn, EvalCheckRow,
  EvalResult, EvalResultRow, EvalRun, EvalRunCreate, EvalRunRow, EvalRunSummaryRow, EvalSuiteRow,
} from "@/shared/types";

export const POLL_MS = 2000;

export const evalKeys = {
  all: ["evals"] as const,
  suite: (agentId: string) => [...evalKeys.all, "suite", agentId] as const,
  run: (runId: string) => [...evalKeys.all, "run", runId] as const,
};

/* ------------------------------ endpoints -------------------------------- */

export async function getSuite(agentId: string): Promise<EvalSuiteRow> {
  return unwrap(await client.get<Envelope<EvalSuiteRow>>("/evals", { params: { agent_id: agentId } }));
}

export async function createCase(body: EvalCaseCreate): Promise<EvalCaseRow> {
  return unwrap(await client.post<Envelope<EvalCaseRow>>("/evals/cases", body));
}

export async function patchCase(caseId: string, body: EvalCasePatch): Promise<EvalCaseRow> {
  return unwrap(await client.patch<Envelope<EvalCaseRow>>(`/evals/cases/${caseId}`, body));
}

/** Archives: the case leaves the suite and stays in the runs that scored it. */
export async function archiveCase(caseId: string): Promise<void> {
  unwrap(await client.delete<Envelope<unknown>>(`/evals/cases/${caseId}`));
}

export async function putChecks(agentId: string, checks: EvalCheckIn[]): Promise<EvalCheckRow[]> {
  return unwrap(await client.put<Envelope<EvalCheckRow[]>>("/evals/checks", { agent_id: agentId, checks }));
}

export async function startRun(body: EvalRunCreate): Promise<EvalRunSummaryRow> {
  return unwrap(await client.post<Envelope<EvalRunSummaryRow>>("/evals/runs", body));
}

export async function getRun(runId: string): Promise<EvalRunRow> {
  return unwrap(await client.get<Envelope<EvalRunRow>>(`/evals/runs/${runId}`));
}

export async function setBaseline(runId: string): Promise<EvalRunSummaryRow> {
  return unwrap(await client.post<Envelope<EvalRunSummaryRow>>(`/evals/runs/${runId}/baseline`));
}

/* -------------------------------- hooks ---------------------------------- */

const running = (runs: { status: string }[] | undefined) => !!runs?.some((r) => r.status === "running");

export const useEvalSuite = (agentId: string | null) =>
  useQuery({
    queryKey: evalKeys.suite(agentId ?? ""),
    queryFn: () => getSuite(agentId!),
    enabled: !!agentId,
    refetchInterval: (query) => (running(query.state.data?.runs) ? POLL_MS : false),
  });

/** Several runs in full. A finished run never changes, so only a running one polls. */
export const useEvalRuns = (runIds: string[]) =>
  useQueries({
    queries: runIds.map((id) => ({
      queryKey: evalKeys.run(id),
      queryFn: () => getRun(id),
      refetchInterval: (query: { state: { data?: EvalRunRow } }) =>
        query.state.data?.status === "running" ? POLL_MS : false,
    })),
  });

export const useEvalRun = (runId: string | null) =>
  useQuery({
    queryKey: evalKeys.run(runId ?? ""),
    queryFn: () => getRun(runId!),
    enabled: !!runId,
    refetchInterval: (query) => (query.state.data?.status === "running" ? POLL_MS : false),
  });

/** Every write re-reads the evals it touched (rule book §3: invalidate by prefix). */
function useEvalMutation<V, T>(mutationFn: (vars: V) => Promise<T>) {
  const qc = useQueryClient();
  return useMutation({ mutationFn, onSettled: () => qc.invalidateQueries({ queryKey: evalKeys.all }) });
}

export const useCreateEvalCase = () => useEvalMutation(createCase);
export const usePatchEvalCase = () =>
  useEvalMutation(({ caseId, body }: { caseId: string; body: EvalCasePatch }) => patchCase(caseId, body));
export const useArchiveEvalCase = () => useEvalMutation(archiveCase);
export const usePutEvalChecks = () =>
  useEvalMutation(({ agentId, checks }: { agentId: string; checks: EvalCheck[] }) =>
    putChecks(agentId, checks.map(fromEvalCheck)));
export const useStartEvalRun = () => useEvalMutation(startRun);
export const useSetEvalBaseline = () => useEvalMutation(setBaseline);

/* ------------------------- the wire → the mockup -------------------------- */

const defined = <T extends object>(o: T): T =>
  Object.fromEntries(Object.entries(o).filter(([, v]) => v !== undefined)) as T;

export function toEvalCheck(row: EvalCheckRow): EvalCheck {
  const c = row.config;
  return defined({
    id: row.id,
    type: row.type,
    weight: row.weight,
    text: c.text,
    pattern: c.pattern,
    keys: c.keys,
    tool: c.tool,
    argMatch: c.arg_match,
    args: c.args,
    tools: c.tools,
    mode: c.mode,
    n: c.n,
    rubric: c.rubric,
    model: c.model,
    connectionId: c.connection_id,
    maxCostUsd: c.max_cost_usd,
    maxMs: c.max_ms,
  });
}

/** A number input arrives as text; empty or not a number means "not set". */
const num = (v: number | string | undefined, { int = false, min = 0 } = {}): number | undefined => {
  if (v === undefined || v === "") return undefined;
  const n = int ? Math.round(Number(v)) : Number(v);
  return Number.isFinite(n) && n >= min ? n : undefined;
};

/** The editor's check as the wire takes it. A setting left empty is kept: it
    saves, and grades as skipped with the reason, as it did in the mockup. */
export function fromEvalCheck(check: EvalCheck): EvalCheckIn {
  return {
    type: check.type,
    weight: num(check.weight) || 1,
    config: defined({
      text: check.text,
      pattern: check.pattern,
      keys: check.keys,
      tool: check.tool,
      arg_match: check.argMatch,
      args: check.args,
      tools: check.tools,
      mode: check.mode,
      n: num(check.n, { int: true, min: 1 }),
      rubric: check.rubric,
      model: check.model || undefined,
      connection_id: check.model ? check.connectionId || undefined : undefined,
      max_cost_usd: num(check.maxCostUsd),
      max_ms: num(check.maxMs, { int: true }),
    }),
  };
}

/** A new case from the editor, for `POST /evals/cases`. */
export function toCaseCreate(agentId: string, kase: EvalCase): EvalCaseCreate {
  return {
    agent_id: agentId,
    name: kase.name,
    input: kase.input,
    expected: kase.expected,
    tags: kase.tags,
    checks: kase.checks.map(fromEvalCheck),
    origin_run_id: kase.origin?.runId ?? null,
  };
}

const ago = (iso: string) => {
  const age = ageLabel(iso);
  return age === "just now" ? age : `${age} ago`;
};

export function toEvalCase(row: EvalCaseRow): EvalCase {
  return {
    id: row.id,
    name: row.name,
    input: row.input,
    expected: row.expected,
    tags: row.tags,
    checks: row.checks.map(toEvalCheck),
    origin: row.origin_run_id ? { runId: row.origin_run_id, spanId: null } : null,
    created: ago(row.created_at),
  };
}

function toEvalResult(row: EvalResultRow, pinned: EvalCheck[]): EvalResult {
  return {
    caseId: row.case_id,
    runId: row.run_id,
    error: row.error,
    output: row.output,
    tools: row.tools,
    toolCalls: row.tool_calls,
    steps: row.steps ?? 0,
    ms: row.ms ?? 0,
    cost: row.cost_usd,
    checks: row.checks.map((c) => {
      const check = pinned.find((p) => p.id === c.id) ?? { id: c.id, type: c.type, weight: c.weight };
      return { ...c, label: CHECK_TYPES[c.type]?.describe(check) ?? c.type };
    }),
    score: row.score,
    status: row.status,
  };
}

/** A run from its summary, and — once loaded — its pinned cases and results. */
export function toEvalRun(summary: EvalRunSummaryRow, detail?: EvalRunRow): EvalRun {
  const pinned = detail ? Object.entries(detail.cases) : [];
  const checksOf = Object.fromEntries(pinned.map(([id, kase]) => [id, kase.checks.map(toEvalCheck)]));
  const { number, published } = summary.version;
  return {
    id: summary.id,
    when: summary.status === "running" ? `running ${summary.graded}/${summary.total}` : ago(summary.created_at),
    label: summary.label,
    target: { version: number == null ? "—" : `v${number}${published === false ? " draft" : ""}`, key: summary.id },
    caseIds: summary.case_ids,
    checks: pinned.map(([caseId]) => ({ caseId, checks: checksOf[caseId] })),
    results: detail ? detail.results.map((r) => toEvalResult(r, checksOf[r.case_id] ?? [])) : [],
    score: summary.score,
    passRate: summary.pass_rate ?? 0,
    cost: summary.cost_usd,
    ms: summary.ms ?? 0,
    status: summary.status,
    error: summary.error,
    total: summary.total,
    graded: summary.graded,
    passed: summary.passed,
    published: published ?? undefined,
    cases: detail ? Object.fromEntries(pinned.map(([id, { name, input, expected }]) => [id, { name, input, expected }])) : undefined,
  };
}
